import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <Layout>
      <SEOHead
        title="Privacy Policy - NoQ5.ai"
        description="How NoQ5 collects, uses, and protects your information across our website and AI-powered CRM assistant. GDPR, CCPA, and Google API Services User Data Policy compliant."
        canonicalUrl="https://noq5.ai/privacy-policy"
      />
      <article className="container px-4 md:px-6 py-12 max-w-3xl mx-auto prose prose-invert prose-headings:text-foreground prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground prose-a:text-primary">
        <header className="not-prose mb-10">
          <p className="text-sm text-muted-foreground">Effective Date: 29 December 2025 · Version 2.1</p>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mt-2">Privacy Policy</h1>
          <p className="text-muted-foreground mt-3">
            NoQ5 ("Company," "we," "us," or "our") is committed to protecting your privacy. This Privacy
            Policy explains how we collect, use, disclose, and safeguard your information when you visit
            noq5.ai or use our AI-powered CRM assistant platform and related services (collectively, the
            "Service").
          </p>
          <p className="text-muted-foreground mt-3">
            Please read this Privacy Policy carefully. By accessing or using the Service, you acknowledge
            that you have read, understood, and agree to be bound by this Privacy Policy. If you do not
            agree with the terms of this Privacy Policy, please do not access or use the Service.
          </p>
        </header>

        <h2>Google API Services User Data Policy</h2>
        <p>
          NoQ5's use and transfer of information received from Google APIs to any other app adheres to the{' '}
          <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>

        <h3>Google Workspace data we access</h3>
        <ul>
          <li><strong>Google Calendar (read & write):</strong> We read your calendar events to surface upcoming meetings, build pre-call briefings, and match meeting transcripts to the correct attendees and CRM records. We write calendar events only when you explicitly schedule a follow-up through NoQ5.</li>
          <li><strong>Gmail (read & send):</strong> We read messages you have exchanged with prospects to build a timeline of touchpoints and detect intent signals (pricing questions, objections, sentiment) that power briefings and recommendations. We send emails on your behalf only when you review and explicitly approve a draft inside NoQ5; emails are sent from your own Gmail address.</li>
          <li><strong>Google Drive (read-only):</strong> We access only Google Meet transcripts stored in your Drive (typically the "Meet Recordings" folder) to extract action items, risks, and CRM updates from meetings you attended. We do not browse, index, or read any other files in your Drive.</li>
          <li><strong>Basic profile (email, name):</strong> Used to identify your account and link your Google identity to your NoQ5 user.</li>
        </ul>

        <h3>How we use, store, and protect Google data</h3>
        <ul>
          <li><strong>Limited Use:</strong> Google user data is used solely to provide and improve the user-facing features described above. We do <strong>not</strong> use Google data to serve advertisements, and we do <strong>not</strong> sell, rent, or transfer Google data to data brokers, advertisers, or any other third parties.</li>
          <li><strong>No human reading:</strong> We do not allow humans to read Google user data except (a) with your explicit consent, (b) for security investigations or to comply with applicable law, or (c) where the data has been aggregated and anonymized.</li>
          <li><strong>No AI model training:</strong> Google user data is never used to train, fine-tune, or develop generalized AI/ML models. AI providers we use (e.g. for transcription and extraction) process data under zero-retention, no-training contractual terms.</li>
          <li><strong>Storage & encryption:</strong> OAuth tokens are encrypted at rest (AES-256-GCM) and transmitted over TLS 1.2+. Data is hosted in the EU (AWS eu-central-1, Frankfurt). Calendar, Gmail, and Drive content is processed on-demand and only the minimal derived insights (e.g. structured CRM updates, meeting summaries) are persisted, with automated retention limits.</li>
          <li><strong>Sub-processors:</strong> Google data may be processed by Supabase (hosting, EU), OpenAI (transcription, zero-retention), and Google Gemini (extraction). None are permitted to use the data for their own purposes. See <Link to="/subprocessors">noq5.ai/subprocessors</Link>.</li>
        </ul>

        <h3>Your control</h3>
        <p>
          You can revoke NoQ5's access to your Google account at any time by disconnecting the integration
          in NoQ5 Settings, or directly via your{' '}
          <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">
            Google Account permissions page
          </a>
          . On disconnection, stored OAuth tokens are deleted and no further Google data is ingested. To
          request deletion of derived data (e.g. meeting summaries), contact{' '}
          <a href="mailto:privacy@noq5.ai">privacy@noq5.ai</a>.
        </p>

        <h2>1. Information We Collect</h2>

        <h3>1.1 Information You Provide to Us</h3>
        <ul>
          <li><strong>Account Information:</strong> When your account is created, we collect your name, email address, phone number, job title, and password.</li>
          <li><strong>Organization Information:</strong> For organization accounts, we collect company name, domain, and organizational settings.</li>
          <li><strong>Payment Information:</strong> If applicable, we collect billing information necessary to process payments. Payment processing is handled by secure third-party payment processors; we do not store complete credit card numbers.</li>
          <li><strong>Demo / waitlist forms:</strong> name, work email, company, and message submitted on noq5.ai.</li>
          <li><strong>Communications:</strong> When you contact us for support or other inquiries, we collect the content of your communications.</li>
        </ul>

        <h3>1.2 Information Collected Through the Service</h3>
        <ul>
          <li><strong>Voice and Text Input:</strong> Voice recordings and text commands you submit through the Service for processing by our AI system.</li>
          <li><strong>Transcription Data:</strong> Text transcriptions generated from your voice inputs.</li>
          <li><strong>CRM Data:</strong> Data retrieved from and written to your connected CRM systems (such as HubSpot, Salesforce, Pipedrive) as directed by your commands.</li>
          <li><strong>Usage Data:</strong> Information about how you interact with the Service, including features used, actions taken, and time spent.</li>
          <li><strong>Session Data:</strong> Information about your sessions, including commands issued and AI-proposed actions.</li>
        </ul>

        <h3>1.3 Information Collected Automatically</h3>
        <ul>
          <li><strong>Device Information:</strong> Device type, operating system, browser type, and unique device identifiers.</li>
          <li><strong>Log Data:</strong> IP address, access times, pages viewed, and the page you visited before navigating to our Service.</li>
          <li><strong>Cookies and Similar Technologies:</strong> We use cookies, local storage, and similar technologies to collect information about your browsing activities. See Section 8 for more details.</li>
        </ul>

        <h3>1.4 Information from Third-Party Services</h3>
        <p>
          When you connect third-party services (such as HubSpot, Salesforce, Pipedrive, Google Workspace,
          Microsoft 365, Zoom, or Slack) to NoQ5, we receive information from those services as authorized
          by you, including CRM records (contacts, companies, deals), user identity information, and
          permission levels. The information we receive depends on your settings and the third party's
          privacy practices.
        </p>

        <h2>2. How We Use Your Information</h2>

        <h3>2.1 Provide and Maintain the Service</h3>
        <ul>
          <li>Process your voice and text commands</li>
          <li>Interpret commands using AI and propose CRM actions</li>
          <li>Execute approved actions in your connected CRM systems</li>
          <li>Authenticate users and manage accounts</li>
          <li>Provide customer support</li>
        </ul>

        <h3>2.2 Improve and Develop the Service</h3>
        <ul>
          <li>Analyze usage patterns to improve functionality</li>
          <li>Develop new features and services</li>
          <li>Conduct research and analytics</li>
          <li>Debug and fix technical issues</li>
        </ul>

        <h3>2.3 Communicate with You</h3>
        <ul>
          <li>Send service-related notices and updates</li>
          <li>Respond to your inquiries and support requests</li>
          <li>Send administrative information, such as changes to our terms or policies</li>
        </ul>

        <h3>2.4 Ensure Security and Compliance</h3>
        <ul>
          <li>Detect, prevent, and address fraud, abuse, or security issues</li>
          <li>Enforce our terms of service and other policies</li>
          <li>Comply with legal obligations</li>
          <li>Maintain audit logs for security and compliance purposes</li>
        </ul>

        <h2>3. Legal Basis for Processing (GDPR)</h2>
        <p>
          If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland, we
          process your personal data based on the following legal grounds:
        </p>
        <ul>
          <li><strong>Performance of Contract:</strong> Processing necessary to provide the Service as agreed in our contract with you or your organization.</li>
          <li><strong>Legitimate Interests:</strong> Processing necessary for our legitimate interests (such as improving the Service, ensuring security, and preventing fraud), where those interests are not overridden by your rights.</li>
          <li><strong>Consent:</strong> Where you have provided consent for specific processing activities (which you may withdraw at any time).</li>
          <li><strong>Legal Obligation:</strong> Processing necessary to comply with legal obligations to which we are subject.</li>
        </ul>

        <h2>4. How We Share Your Information</h2>
        <p>We do not sell your personal information. We may share your information in the following circumstances:</p>

        <h3>4.1 Service Providers</h3>
        <p>We share information with third-party service providers who perform services on our behalf, including:</p>
        <ul>
          <li>Cloud infrastructure and hosting providers</li>
          <li>AI and speech processing services</li>
          <li>Analytics providers</li>
          <li>Payment processors</li>
          <li>Customer support tools</li>
        </ul>
        <p>
          These providers are contractually obligated to protect your information and may only use it to
          provide services to us. The current list is published at{' '}
          <Link to="/subprocessors">noq5.ai/subprocessors</Link>.
        </p>

        <h3>4.2 Third-Party Integrations</h3>
        <p>
          When you connect third-party services (such as HubSpot, Salesforce, Pipedrive) to NoQ5, we share
          information with those services as necessary to perform the actions you request. This sharing is
          governed by your authorization and the third party's privacy policy.
        </p>

        <h3>4.3 Within Your Organization</h3>
        <p>
          If you use the Service as part of an organization, administrators and other authorized users
          within your organization may have access to certain information about your use of the Service.
        </p>

        <h3>4.4 Legal Requirements</h3>
        <p>
          We may disclose your information if required to do so by law or in response to valid requests by
          public authorities (e.g., a court order, subpoena, or government request).
        </p>

        <h3>4.5 Business Transfers</h3>
        <p>
          If we are involved in a merger, acquisition, financing, reorganization, bankruptcy, or sale of
          assets, your information may be transferred as part of that transaction. We will notify you of
          any change in ownership or uses of your information.
        </p>

        <h3>4.6 With Your Consent</h3>
        <p>We may share your information for other purposes with your explicit consent.</p>

        <h2>5. Data Retention</h2>
        <p>
          We retain your information for as long as necessary to fulfill the purposes described in this
          Privacy Policy, unless a longer retention period is required or permitted by law.
        </p>

        <h3>5.1 Account Data</h3>
        <p>
          We retain account information for the duration of your account and for a reasonable period
          thereafter to comply with legal obligations, resolve disputes, and enforce our agreements.
        </p>

        <h3>5.2 Voice Recordings</h3>
        <p>
          Voice recordings are processed for transcription and are not retained permanently. Raw audio
          data is automatically deleted after processing, typically within 24-48 hours.
        </p>

        <h3>5.3 Session and Command Data</h3>
        <p>
          Session data, including commands and AI-proposed actions, is retained for a limited period
          (typically 30-90 days) for debugging, auditing, and service improvement purposes, after which it
          is automatically deleted or anonymized.
        </p>

        <h3>5.4 Audit Logs</h3>
        <p>
          Security and audit logs are retained for compliance and security purposes in accordance with
          applicable legal requirements and industry standards.
        </p>

        <h2>6. Data Security</h2>
        <p>We implement robust technical and organizational security measures to protect your information:</p>
        <ul>
          <li><strong>Encryption in Transit:</strong> All data transmitted between your device and our servers is encrypted using TLS 1.2 or higher.</li>
          <li><strong>Encryption at Rest:</strong> Sensitive data stored in our systems is encrypted using AES-256-GCM encryption.</li>
          <li><strong>Access Controls:</strong> We implement strict access controls to limit access to personal data to authorized personnel only.</li>
          <li><strong>OAuth Token Security:</strong> Integration tokens (such as HubSpot, Salesforce, and Google OAuth tokens) are encrypted and never exposed to client applications.</li>
          <li><strong>Infrastructure Security:</strong> Our infrastructure is hosted on enterprise-grade cloud platforms (EU regions) with industry-standard security certifications.</li>
          <li><strong>Regular Security Assessments:</strong> We conduct regular security reviews and vulnerability assessments. ISO 27001 certification in progress.</li>
        </ul>
        <p>
          While we strive to protect your information, no method of transmission over the Internet or
          electronic storage is 100% secure. We cannot guarantee absolute security, but we are committed
          to implementing and maintaining reasonable security measures. See{' '}
          <Link to="/security">noq5.ai/security</Link> for full details.
        </p>

        <h2>7. Your Privacy Rights</h2>
        <p>Depending on your location, you may have certain rights regarding your personal information:</p>

        <h3>7.1 Rights for All Users</h3>
        <ul>
          <li><strong>Access:</strong> Request a copy of the personal information we hold about you.</li>
          <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information.</li>
          <li><strong>Deletion:</strong> Request deletion of your personal information, subject to certain exceptions.</li>
          <li><strong>Disconnect Integrations:</strong> Disconnect third-party integrations at any time through your account settings.</li>
        </ul>

        <h3>7.2 Additional Rights for EEA/UK Residents (GDPR)</h3>
        <ul>
          <li><strong>Data Portability:</strong> Receive your personal data in a structured, machine-readable format.</li>
          <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances.</li>
          <li><strong>Objection:</strong> Object to processing based on legitimate interests.</li>
          <li><strong>Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent.</li>
          <li><strong>Lodge a Complaint:</strong> Lodge a complaint with a supervisory authority (e.g. the Irish Data Protection Commission).</li>
        </ul>

        <h3>7.3 Additional Rights for California Residents (CCPA/CPRA)</h3>
        <ul>
          <li><strong>Know:</strong> Know what personal information we collect and how we use it.</li>
          <li><strong>Delete:</strong> Request deletion of your personal information.</li>
          <li><strong>Correct:</strong> Request correction of inaccurate personal information.</li>
          <li><strong>Non-Discrimination:</strong> Not be discriminated against for exercising your privacy rights.</li>
          <li><strong>Opt-Out of Sale:</strong> We do not sell personal information, so no opt-out is necessary.</li>
        </ul>

        <h3>7.4 Exercising Your Rights</h3>
        <p>
          To exercise any of these rights, please contact us at{' '}
          <a href="mailto:privacy@noq5.ai">privacy@noq5.ai</a> or{' '}
          <a href="mailto:hello@noq5.ai">hello@noq5.ai</a>. We will respond to your request within the
          timeframe required by applicable law (typically 30 days). We may need to verify your identity
          before processing your request.
        </p>

        <h2>8. Cookies and Tracking Technologies</h2>
        <p>We use cookies and similar technologies to operate and improve the Service:</p>

        <h3>8.1 Types of Cookies We Use</h3>
        <ul>
          <li><strong>Essential Cookies:</strong> Required for the Service to function properly (authentication, security, session management).</li>
          <li><strong>Functional Cookies:</strong> Remember your preferences and settings to enhance your experience.</li>
          <li><strong>Analytics Cookies:</strong> Help us understand how users interact with the Service to improve functionality (Google Analytics 4, consent-based in regions requiring it).</li>
          <li><strong>Marketing Pixels:</strong> LinkedIn Insight, Meta Pixel, and Google Ads pixels fire only after consent in regions where required.</li>
        </ul>

        <h3>8.2 Managing Cookies</h3>
        <p>
          Most web browsers allow you to control cookies through their settings. However, disabling
          certain cookies may limit your ability to use some features of the Service.
        </p>

        <h2>9. International Data Transfers</h2>
        <p>
          Your information may be transferred to and processed in countries other than your country of
          residence. These countries may have data protection laws that are different from the laws of
          your country.
        </p>
        <p>
          Production data is hosted in the European Union (Ireland / Frankfurt). When we transfer personal
          data from the EEA, UK, or Switzerland to other countries, we implement appropriate safeguards to
          ensure your information remains protected, including:
        </p>
        <ul>
          <li>Standard Contractual Clauses approved by the European Commission (2021/914)</li>
          <li>Transfers to countries with an adequacy decision</li>
          <li>Other legally approved transfer mechanisms</li>
        </ul>

        <h2>10. Third-Party Services and Links</h2>
        <p>
          The Service integrates with third-party services and may contain links to third-party websites.
          This Privacy Policy does not apply to third-party services or websites.
        </p>
        <p>
          <strong>CRM and Workspace Integrations:</strong> When you connect HubSpot, Salesforce, Pipedrive,
          Google Workspace, Microsoft 365, Zoom, or Slack to NoQ5, your use of those services is governed
          by their respective privacy policies. We encourage you to review the privacy policies of any
          third-party services you connect to NoQ5.
        </p>

        <h2>11. Children's Privacy</h2>
        <p>
          The Service is not intended for use by children under the age of 18. We do not knowingly collect
          personal information from children under 18. If we learn that we have collected personal
          information from a child under 18, we will take steps to delete that information as soon as
          possible. If you believe that a child under 18 has provided us with personal information, please
          contact us at <a href="mailto:privacy@noq5.ai">privacy@noq5.ai</a>.
        </p>

        <h2>12. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. If we make material changes, we will notify
          you by email (sent to the email address associated with your account) or by posting a prominent
          notice on the Service prior to the changes taking effect. We encourage you to review this
          Privacy Policy periodically for any changes. Your continued use of the Service after any changes
          to this Privacy Policy constitutes your acceptance of the updated policy.
        </p>

        <h2>13. Data Protection Officer</h2>
        <p>
          For questions or concerns about our privacy practices or to exercise your data protection rights,
          you may contact our Data Protection team at:
        </p>
        <p>
          NoQ5 Data Protection<br />
          Email: <a href="mailto:privacy@noq5.ai">privacy@noq5.ai</a>
        </p>

        <h2>14. Contact Us</h2>
        <p>If you have any questions about this Privacy Policy or our privacy practices, please contact us:</p>
        <p>
          NoQ5.ai Technologies Ltd. · Dublin, Ireland<br />
          Email: <a href="mailto:hello@noq5.ai">hello@noq5.ai</a><br />
          Privacy Inquiries: <a href="mailto:privacy@noq5.ai">privacy@noq5.ai</a><br />
          Website: <a href="https://noq5.ai/">noq5.ai</a>
        </p>

        <h2>Summary of Data Practices</h2>
        <div className="not-prose overflow-x-auto rounded-2xl border border-border my-6">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-foreground">
              <tr>
                <th className="text-left p-4 font-semibold">Category</th>
                <th className="text-left p-4 font-semibold">Data Collected</th>
                <th className="text-left p-4 font-semibold">Purpose</th>
                <th className="text-left p-4 font-semibold">Retention</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-border">
                <td className="p-4 font-medium text-foreground">Account</td>
                <td className="p-4 text-muted-foreground">Name, email, password</td>
                <td className="p-4 text-muted-foreground">Authentication, communication</td>
                <td className="p-4 text-muted-foreground">Duration of account + legal period</td>
              </tr>
              <tr className="border-t border-border">
                <td className="p-4 font-medium text-foreground">Voice Input</td>
                <td className="p-4 text-muted-foreground">Audio recordings</td>
                <td className="p-4 text-muted-foreground">Transcription, command processing</td>
                <td className="p-4 text-muted-foreground">Deleted after processing (24-48 hrs)</td>
              </tr>
              <tr className="border-t border-border">
                <td className="p-4 font-medium text-foreground">CRM Data</td>
                <td className="p-4 text-muted-foreground">Data from HubSpot, Salesforce, Pipedrive</td>
                <td className="p-4 text-muted-foreground">Display, update per your commands</td>
                <td className="p-4 text-muted-foreground">Cached temporarily; source in CRM</td>
              </tr>
              <tr className="border-t border-border">
                <td className="p-4 font-medium text-foreground">Google Workspace</td>
                <td className="p-4 text-muted-foreground">Calendar, Gmail, Drive (Meet transcripts)</td>
                <td className="p-4 text-muted-foreground">Briefings, meeting summaries, CRM updates</td>
                <td className="p-4 text-muted-foreground">Derived insights only; tokens deleted on disconnect</td>
              </tr>
              <tr className="border-t border-border">
                <td className="p-4 font-medium text-foreground">Session Data</td>
                <td className="p-4 text-muted-foreground">Commands, AI-proposed actions</td>
                <td className="p-4 text-muted-foreground">Debugging, auditing, improvement</td>
                <td className="p-4 text-muted-foreground">30-90 days then deleted/anonymized</td>
              </tr>
              <tr className="border-t border-border">
                <td className="p-4 font-medium text-foreground">Usage & Device</td>
                <td className="p-4 text-muted-foreground">IP, browser, pages viewed</td>
                <td className="p-4 text-muted-foreground">Analytics, security</td>
                <td className="p-4 text-muted-foreground">Up to 14 months</td>
              </tr>
            </tbody>
          </table>
        </div>
      </article>
    </Layout>
  );
};

export default PrivacyPolicy;
